/**
 * Spec 103 — Rule Proposals review queue.
 *
 * Lists synthesized new-rule proposals (clustered candidates) with a status
 * filter. Each proposal renders as a ProposalCard showing the proposed rule
 * fields, cohesion, an overlap warning when the cluster centroid is near an
 * existing rule, and an expandable member list (each member -> source link =
 * provenance). Approve creates a real active Rule; Reject releases members.
 *
 * data-testid attributes wire gate 103.A.7:
 *   proposal-card, proposal-member, proposal-member-source,
 *   proposal-approve, proposal-reject, proposal-overlap-warning
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { Icon } from '../../components/ui/Icon.js';
import {
  approveProposal,
  listProposals,
  rejectProposal,
  type RuleProposal,
  type RuleProposalStatus,
} from '../../api/ruleIngestion.js';
import { useToast } from '../../hooks/useToast.js';
import { Breadcrumbs } from '../../components/Breadcrumbs.js';
import { RoleProvisioningBanner } from '../../components/RoleProvisioningBanner.js';
import { Select } from '../../components/Select.js';
import { RowList, Row, RowFacts, RowFact } from '../../components/ui/Row.js';

const STATUS_OPTIONS: RuleProposalStatus[] = ['pending', 'approved', 'rejected'];

export function RuleProposalsPage() {
  const toast = useToast();
  const [status, setStatus] = useState<RuleProposalStatus>('pending');
  const [rows, setRows] = useState<RuleProposal[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErr(null);
    listProposals({ status, limit: 50 })
      .then((res) => {
        if (cancelled) return;
        setRows(res.data);
        setTotal(res.total);
      })
      .catch((e) => {
        if (cancelled) return;
        setErr(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [status, reloadToken]);

  async function handleApprove(p: RuleProposal) {
    try {
      const res = await approveProposal(p.id);
      toast.addToast(
        res.idempotent
          ? `Already approved (rule ${res.rule_slug ?? res.rule_id ?? ''})`
          : `Approved — created rule ${res.rule_slug ?? res.rule_id ?? ''}`,
        'success',
      );
      setReloadToken((t) => t + 1);
    } catch (e) {
      toast.addToast(e instanceof Error ? e.message : String(e), 'error');
    }
  }

  async function handleReject(p: RuleProposal) {
    try {
      const res = await rejectProposal(p.id);
      toast.addToast(
        `Rejected — ${res.members_released} member${res.members_released === 1 ? '' : 's'} returned to pending`,
        'success',
      );
      setReloadToken((t) => t + 1);
    } catch (e) {
      toast.addToast(e instanceof Error ? e.message : String(e), 'error');
    }
  }

  return (
    <div data-testid="admin-rule-proposals" className="hub-page--table">
      <RoleProvisioningBanner />
      <Breadcrumbs
        items={[{ label: 'Observatory', to: '/admin' }, { label: 'Rule proposals' }]}
      />
      <header>
        <h1 className="hub-page__title flex items-center gap-2">
          <Icon name="bx-git-merge" className="text-violet-600" />
          Rule proposals
        </h1>
        <p className="text-sm text-slate-600 mt-1">
          New rules synthesized from clusters of corroborating community candidates. Approve to
          create an active rule (provenance preserved); reject to return members to pending.
        </p>
      </header>

      <div className="hub-plate">
        <label className="text-sm">
          <span className="block text-slate-600 mb-1">Status</span>
          <Select
            data-testid="proposals-filter-status"
            value={status}
            onChange={(v) => setStatus(v as RuleProposalStatus)}
            options={STATUS_OPTIONS.map((s) => ({ value: s, label: s }))}
            className="w-48"
          />
        </label>
      </div>

      {err && (
        <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
          {err}
        </div>
      )}

      {loading && <div className="text-sm text-slate-500">Loading…</div>}

      {!loading && rows.length === 0 && (
        <div className="hub-plate hub-plate--flat text-center text-sm text-slate-500">
          No {status} proposals. Cluster pending candidates from the Rule candidates page to create
          proposals.
        </div>
      )}

      {rows.length > 0 && (
        <RowList>
          {rows.map((p) => (
            <ProposalCard
              key={p.id}
              proposal={p}
              onApprove={() => handleApprove(p)}
              onReject={() => handleReject(p)}
            />
          ))}
        </RowList>
      )}

      {!loading && total > 0 && (
        <div className="text-sm text-slate-500">{total} total</div>
      )}
    </div>
  );
}

interface ProposalCardProps {
  proposal: RuleProposal;
  onApprove: () => void;
  onReject: () => void;
}

function ProposalCard({ proposal: p, onApprove, onReject }: ProposalCardProps) {
  const [expanded, setExpanded] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const members = p.members ?? [];
  const evalSteps = p.proposed_evaluation_steps as { evaluation_steps?: unknown };
  const steps = Array.isArray(evalSteps?.evaluation_steps)
    ? (evalSteps.evaluation_steps as unknown[]).map((s) => String(s))
    : [];

  async function wrap(fn: () => void) {
    if (submitting) return;
    setSubmitting(true);
    try {
      await Promise.resolve(fn());
    } finally {
      setSubmitting(false);
    }
  }

  return (
    // Spec 235 — the hardest row in the spec: two real links (the overlap
    // warning's rule link, and one member-source link per expanded member)
    // plus three buttons (the member-list toggle, approve, reject), and NO
    // reliable per-proposal detail page for the common case. `to` is
    // conditional on `created_rule_id`: a pending or rejected proposal has
    // nowhere to go (disclosure — no anchor, no overlay), while an approved
    // one navigates to the rule it created. That split is not a workaround;
    // it is why `to` became optional in the first place (spec 235 §3.3), and
    // it happens to be mutually exclusive with the approve/reject buttons —
    // those only render for `pending`, which never has a `to`, so they never
    // need a z-index lift. What DOES need `.hub-row__interactive` is the
    // rule-overlap link and each member-source link: whenever a row DOES
    // have `to`, the stretched `::after` covers the whole `<li>`, not just
    // `top`/`actions`, so anything nested in `children` sits under it too.
    <Row
      to={p.created_rule_id ? `/admin/rules/${p.created_rule_id}` : undefined}
      data-testid="proposal-card"
      title={p.proposed_name}
      actions={
        p.status === 'pending' ? (
          <>
            <button
              type="button"
              data-testid="proposal-approve"
              onClick={() => wrap(onApprove)}
              disabled={submitting}
              className="hub-btn hub-btn--ghost"
            >
              <Icon name="bx-check" /> Approve
            </button>
            <button
              type="button"
              data-testid="proposal-reject"
              onClick={() => wrap(onReject)}
              disabled={submitting}
              className="hub-btn hub-btn--ghost"
            >
              <Icon name="bx-x" /> Reject
            </button>
          </>
        ) : null
      }
    >
      <p className="text-sm text-slate-700">{p.proposed_definition}</p>
      <RowFacts>
        <RowFact icon="bx-collection">Cohesion {p.cohesion.toFixed(3)}</RowFact>
        <RowFact icon="bx-group">
          {members.length} member{members.length === 1 ? '' : 's'}
        </RowFact>
        <RowFact icon="bx-info-circle">{p.synthesis_source}</RowFact>
      </RowFacts>

      {steps.length > 0 && (
        <div className="text-sm text-slate-600">
          <span className="font-medium text-slate-700">Evaluation steps:</span>
          <ul className="mt-1 list-disc pl-5 space-y-0.5">
            {steps.map((s, i) => (
              <li key={i}>{s}</li>
            ))}
          </ul>
        </div>
      )}

      {p.proposed_content_types.length > 0 && (
        <div className="text-xs text-slate-500">
          content types: {p.proposed_content_types.join(', ')}
        </div>
      )}

      {p.overlaps_existing_rule_id && (
        <div
          data-testid="proposal-overlap-warning"
          className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800"
        >
          <Icon name="bx-error" className="mt-0.5 flex-shrink-0" />
          <span>
            Overlaps existing rule{' '}
            {p.overlaps_existing_rule_slug ? (
              // The second real link. A sibling of Row's own stretched anchor
              // in the DOM either way, but only needs lifting above its
              // ::after when this row HAS one (an approved proposal) — the
              // class is harmless to apply unconditionally, so it is.
              <Link
                to={`/admin/rules?slug=${p.overlaps_existing_rule_slug}`}
                className="hub-row__interactive font-medium underline"
              >
                {p.overlaps_existing_rule_name ?? p.overlaps_existing_rule_slug}
              </Link>
            ) : (
              'X'
            )}{' '}
            ({(p.overlaps_existing_rule_similarity ?? 0).toFixed(2)}). Consider rejecting and
            routing the signal to the modification path instead.
          </span>
        </div>
      )}

      <div>
        {/* Do not reword this toggle's copy — .playwright-cli/103-proposals-page.mjs
            selects it by the rendered /Show \d+ member/ text, not just the testid. */}
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="hub-row__interactive inline-flex items-center gap-1 text-sm font-medium text-violet-600 hover:underline"
        >
          {expanded ? <Icon name="bx-chevron-down" /> : <Icon name="bx-chevron-right" />}
          {expanded ? 'Hide' : 'Show'} {members.length} member candidate
          {members.length === 1 ? '' : 's'}
        </button>
        {expanded && (
          <ul className="mt-2 space-y-2 border-l-2 border-slate-100 pl-3">
            {members.map((m) => (
              <li key={m.candidate_id} data-testid="proposal-member" className="text-sm">
                <div className="font-medium text-slate-800">{m.criterion}</div>
                <a
                  href={m.source_post_url}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="proposal-member-source"
                  className="hub-row__interactive text-xs text-violet-600 hover:underline break-all"
                >
                  {m.source_post_author ? `${m.source_post_author} — ` : ''}
                  {m.source_post_url}
                </a>
              </li>
            ))}
          </ul>
        )}
      </div>

      {p.status !== 'pending' && (
        <div className="border-t border-slate-100 pt-3 text-sm text-slate-500">
          {p.status === 'approved' ? (
            <span>
              Approved
              {p.created_rule_id ? ' — rule created' : ''}
              {p.reviewed_by ? ` by ${p.reviewed_by}` : ''}
            </span>
          ) : (
            <span>Rejected{p.reviewed_by ? ` by ${p.reviewed_by}` : ''}</span>
          )}
        </div>
      )}
    </Row>
  );
}
